import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandList } from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  OPERACIONES,
  type Operacion,
} from "@/lib/search-options";
import {
  bestLocationMatch,
  fetchSearchLocations,
  filterLocations,
  type FilterLocationsOptions,
  type LocSuggestion,
} from "@/lib/search-locations";
import { LocationOptions } from "@/components/site/LocationOptions";
import { useNavigate } from "@tanstack/react-router";
import { TipoLocalCombobox, type TipoLocalSuggestion } from "@/components/site/TipoLocalCombobox";

const ONE_DAY = 24 * 60 * 60 * 1000;

type Suggestion = TipoLocalSuggestion;



export function SearchBar({ basePath = "/portal", demo = false }: { basePath?: string; demo?: boolean } = {}) {
  const navigate = useNavigate();
  const [operacion, setOperacion] = useState<Operacion>("traspaso");

  // qué buscas
  const [selected, setSelected] = useState<Suggestion | null>(null);

  // dónde buscas
  const [q, setQ] = useState("");
  const [locOpen, setLocOpen] = useState(false);
  const [locSelected, setLocSelected] = useState<LocSuggestion | null>(null);




  // Cargar ubicaciones sólo cuando se abre el popover de ubicación
  const locationsQuery = useQuery({
    queryKey: ["search-locations"],
    enabled: locOpen,
    staleTime: ONE_DAY,
    queryFn: fetchSearchLocations,
  });
  const locations = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);




  // En el portal público solo se ofrecen ubicaciones con anuncios.
  const locOpts: FilterLocationsOptions = useMemo(
    () => ({ onlyWithListings: basePath === "/portal" && !demo }),
    [basePath, demo],
  );

  // Filtrar ubicaciones (limit para no abrumar)
  const locFiltered = useMemo(
    () => filterLocations(locations, q, 50, locOpts),
    [locations, q, locOpts],
  );

  const handleLocSelect = (loc: LocSuggestion) => {
    setLocSelected(loc);
    setQ(loc.nombre);
    setLocOpen(false);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const params = new URLSearchParams();

    // Ubicación: si hay una seleccionada, usar el filtro correspondiente. Si
    // se escribió texto sin elegir sugerencia ("leganes" + Buscar), usamos la
    // mejor coincidencia: el portal no filtra por `q` y el texto se perdía.
    const loc = locSelected ?? bestLocationMatch(locations, q, locOpts);
    if (loc) {
      if (loc.kind === "provincia") params.append("provs", loc.slug);
      else if (loc.kind === "comarca") {
        // El slug de una comarca solo es único dentro de su provincia.
        if (loc.provinciaSlug) params.append("provs", loc.provinciaSlug);
        params.append("comarcas", loc.slug);
      } else if (loc.kind === "ciudad") params.append("ciudades", loc.slug);
      else if (loc.kind === "barrio") params.append("barrios", loc.slug);
      else if (loc.kind === "distrito") params.append("distritos", loc.id);
    } else if (q.trim()) {
      params.append("q", q.trim());
    }

    if (demo) params.append("demo", "true");

    if (selected?.kind === "uso") {
      const search = params.toString();
      navigate({
        to: `${basePath}/${operacion}/${selected.slug}${search ? `?${search}` : ""}`,
      });
      return;
    }

    if (selected?.kind === "tipo") {
      params.append("tipos", selected.id);
    }

    const search = params.toString();
    navigate({
      to: `${basePath}/${operacion}${search ? `?${search}` : ""}`,
    });
  };

  return (
    <form
      onSubmit={onSubmit}
      className="w-full max-w-4xl mx-auto rounded-3xl bg-card border border-border shadow-[0_24px_60px_-24px_rgba(0,195,137,0.25)] p-3 sm:p-4"
    >
      {/* Operación tabs */}
      <div
        role="tablist"
        aria-label="Tipo de operación"
        className="flex flex-wrap gap-2 mb-3 border-2 border-black rounded-3xl p-2"
      >
        {OPERACIONES.map((op) => {
          const active = op.value === operacion;
          return (
            <button
              key={op.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setOperacion(op.value)}
              className={cn(
                "flex-1 min-w-fit px-4 py-2.5 text-sm font-semibold rounded-full transition-all",
                active
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground border border-gray-300"
              )}
            >
              {op.label}
            </button>
          );
        })}
      </div>

      {/* Row: qué buscas + ubicación + botón */}
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_auto] gap-2">
      <TipoLocalCombobox value={selected} onChange={setSelected} />


        <Popover open={locOpen} onOpenChange={setLocOpen}>
          <div className="relative flex items-center">
            <Search
              className="absolute left-4 h-5 w-5 text-muted-foreground pointer-events-none z-10"
              aria-hidden
            />
            <PopoverTrigger asChild>
              <input
                type="text"
                role="combobox"
                aria-expanded={locOpen}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setLocSelected(null);
                  if (!locOpen) setLocOpen(true);
                }}
                onFocus={() => setLocOpen(true)}
                placeholder="Escribe dónde buscas"
                aria-label="Ubicación"
                className="w-full h-11 rounded-2xl border-2 border-black bg-background pl-11 pr-4 text-base outline-none focus:border-primary focus:ring-4 focus:ring-primary/15 transition placeholder:text-muted-foreground"
              />
            </PopoverTrigger>
          </div>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command shouldFilter={false}>
              <CommandList>
                <LocationOptions items={locFiltered} onSelect={handleLocSelect} showCount />
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Button
          type="submit"
          size="lg"
          className="h-11 px-8 rounded-2xl text-base font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          <Search className="h-5 w-5 mr-1" />
          Buscar
        </Button>
      </div>
    </form>
  );
}
